import React, { useState, useEffect, useRef } from 'react';
import Lenis from 'lenis';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { LenisProvider } from './context/LenisContext';
import { AnimatePresence } from 'motion/react';
import AuthPage from './pages/AuthPage';
import ReferralPage from './pages/ReferralPage';
import PartnershipPage from './pages/PartnershipPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import ReportsPage from './pages/ReportsPage';
import TradesPage from './pages/TradesPage';
import NotebookPage from './pages/NotebookPage';
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import ChangelogPage from './pages/ChangelogPage';
import DashboardDemo from './pages/DashboardDemo';
import TradesDemo from './pages/TradesDemo';
import PreviewDashboard from './pages/PreviewDashboard';
import AIAssistantPage from './pages/AIAssistantPage';
import FAQPage from './pages/FAQPage';
import NewsPage from './pages/NewsPage';
import DevPicksPage from './pages/DevPicksPage';
import TrendingTokensPage from './pages/TrendingTokensPage';
import TopTradersPage from './pages/TopTradersPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AddManualTradeWizard from './components/AddManualTradeWizard';
import AddDexTradeWizard from './components/AddDexTradeWizard';
import UpdateNotification from './components/UpdateNotification';
import NeoAIWidget from './components/NeoAIWidget';
import { auth } from './services/firebase';
import { PortfolioProvider, usePortfolio } from './context/PortfolioContext';
import { CexAccountProvider, useCexAccount } from './context/CexAccountContext';
import { SubscriptionProvider, useSubscription } from './context/SubscriptionContext';
import { ThemeProvider } from './context/ThemeContext';
import { SidebarProvider } from './context/SidebarContext';
import { apiService } from './services/apiService';
import { API_BASE_URL } from './config/api';
import PremiumGuard from './components/PremiumGuard';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const lenisRef = useRef<Lenis | null>(null);
  const { setPortfolioData, setUserName, setLastSyncedAccountId } = usePortfolio();
  const { effectivePlan, isLoading: isSubLoading } = useSubscription();

  const [isFirebaseVerified, setIsFirebaseVerified] = useState<boolean | null>(null);

  useEffect(() => {
    const lenis = new Lenis();
    lenisRef.current = lenis;
    let frameId: number;

    const raf = (time: number) => {
      lenis.raf(time);
      frameId = requestAnimationFrame(raf);
    };

    frameId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(frameId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    lenisRef.current?.scrollTo(0, { immediate: true });
  }, [location]);

	useEffect(() => {
		const unsubscribe = auth.onAuthStateChanged((user) => {
			if (user) {
				setIsFirebaseVerified(user.emailVerified);
			} else {
				setIsFirebaseVerified(null);
			}
		});
		return () => unsubscribe();
	}, []);


  useEffect(() => {
    const publicPaths = ['/', '/pricing', '/register', '/login', '/about', '/contact', '/privacy', '/terms', '/faq', '/changelog', '/verify-email', '/demo/dashboard', '/demo/trades', '/preview/dashboard', '/partnership', '/partnership/'];
    const path = window.location.pathname.replace(/\/$/, '') || '/';
    const isPublic = publicPaths.includes(path);
    const token = localStorage.getItem('auth_token');

    // 1. If already logged in (token present) and trying to access /register or /login
    if (token && (path === '/register' || path === '/login')) {
      const hasCex = localStorage.getItem('has_cex_accounts') === 'true';
      if (hasCex) {
        console.warn('[App] Already logged in with CEX accounts. Redirecting to dashboard...');
        navigate('/dashboard', { replace: true });
      } else {
        console.warn('[App] Already logged in but onboarding incomplete. Redirecting to onboarding...');
        navigate('/onboarding', { replace: true });
      }
      return;
    }

    // 2. If trial expired, redirect to /pricing
    if (token && effectivePlan === 'expired' && !isPublic && !isSubLoading) {
      console.warn('[App] Trial expired. Redirecting to pricing...');
      navigate('/pricing', { replace: true });
      return;
    }

    // 3. If email is not verified on Firebase, redirect to /verify-email
    if (token && isFirebaseVerified === false && !isPublic) {
      console.warn('[App] Email not verified. Redirecting to /verify-email...');
      navigate('/verify-email', { replace: true });
      return;
    }

    // 4. If not logged in and trying to access a private path, redirect to /login
    if (!token && !isPublic) {
      console.warn('[App] Not logged in. Redirecting to login...');
      navigate('/login', { replace: true });
    }
  }, [effectivePlan, navigate, isSubLoading, isFirebaseVerified, location]);

  const [step, setStep] = useState(() => Number(localStorage.getItem('app_step')) || 1);
  const [subStep, setSubStep] = useState(() => Number(localStorage.getItem('app_substep')) || 1);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const [pendingTrade, setPendingTrade] = useState<any | null>(null);
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem('user_form_data');
    return saved ? JSON.parse(saved) : {
      userId: '',
      name: '',
      email: '',
      password: '',
      traderType: '',
      gender: '',
      birthYear: '',
      goal: '',
      sources: [] as string[],
      plan: 'free',
      broker: '',
      method: 'auto' as 'auto' | 'manual',
      apiKey: '',
      apiSecret: '',
      apiPassphrase: '',
      referralCode: '',
      trade: {
        symbol: '',
        direction: 'Long',
        date: '2026-04-29',
        time: '14:30:00',
        quantity: '',
        price: '',
        commissions: '',
        fees: ''
      }
    };
  });

  // Sync state to localStorage
  useEffect(() => {
    localStorage.setItem('user_form_data', JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    localStorage.setItem('app_step', step.toString());
  }, [step]);

  useEffect(() => {
    localStorage.setItem('app_substep', subStep.toString());
  }, [subStep]);

  // Parse referral code from URL query parameter (?refcode=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get('refcode') || params.get('refCode') || params.get('ref');
    if (refCode && refCode.trim() !== "") {
      const cleanCode = refCode.trim().toUpperCase();
      localStorage.setItem('prefilled_ref_code', cleanCode);
      updateFormData({ referralCode: cleanCode });
    } else {
      // If no query parameter in the current URL, but we have a saved prefilled code in localStorage, prefill it!
      const savedCode = localStorage.getItem('prefilled_ref_code');
      if (savedCode && !formData.referralCode) {
        updateFormData({ referralCode: savedCode });
      }
    }
  }, [location.search, formData.referralCode]);

  // Keep userName context updated with formData.name
  useEffect(() => {
    if (formData.name) {
      setUserName(formData.name);
    }
  }, [formData.name, setUserName]);



  // Auto-recovery of user profile on mount if token is present but userId is empty
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token && !formData.userId) {
      fetch(`${API_BASE_URL}/api/user`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })
      .then(res => {
        if (res.ok) return res.json();
        throw new Error('Failed to fetch user profile');
      })
      .then(user => {
        if (user && user.id) {
          updateFormData({
            userId: user.id,
            name: user.name,
            email: user.email
          });
        }
      })
      .catch(err => {
        console.error('[App] Error auto-recovering user profile on mount:', err);
      });
    }
  }, [formData.userId]);

  const updateFormData = (data: Partial<typeof formData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const updateTradeData = (data: Partial<typeof formData['trade']>) => {
    setFormData(prev => ({
      ...prev,
      trade: { ...prev.trade, ...data }
    }));
  };

  const nextStep = () => {
    if (step === 3) {
      setStep(5);
    } else if (step === 5 && subStep < 2) {
      setSubStep(prev => prev + 1);
    } else {
      setStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (step === 5 && subStep > 1) {
      setSubStep(prev => prev - 1);
    } else if (step === 5 && subStep === 1) {
      setStep(3);
    } else if (step > 2) {
      setStep(prev => prev - 1);
    } else if (step === 2) {
      const token = localStorage.getItem('auth_token');
      if (token) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('has_cex_accounts');
      }
      navigate('/register');
    }
  };

  const handleRegister = async () => {
    const response = await fetch(`${API_BASE_URL}/api/check-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        email: formData.email
      })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Registration failed');
    }

    setUserName(formData.name);
    setStep(2);
  };

  const handleSelectPlan = async (planId: string) => {
    // 1. Update the plan locally
    updateFormData({ plan: planId });

    if (planId === 'pro' || planId === 'premium') {
      setIsConnecting(true);
      setConnectionError('');
      try {
        let resolvedUserId = formData.userId;
        const token = localStorage.getItem('auth_token');
        
        // Dynamic lookup of user details if missing but token is available
        if (!resolvedUserId && token) {
          const userRes = await fetch(`${API_BASE_URL}/api/user`, {
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          if (userRes.ok) {
            const userData = await userRes.json();
            resolvedUserId = userData.id;
            updateFormData({ userId: userData.id, name: userData.name, email: userData.email });
          }
        }

        let response;
        if (!resolvedUserId) {
          // If still no userId, fallback to register-complete (with empty password)
          response = await fetch(`${API_BASE_URL}/api/register-complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              name: formData.name,
              email: formData.email,
              password: formData.password,
              trader_type: formData.traderType,
              gender: formData.gender,
              birth_year: formData.birthYear,
              primary_goal: formData.goal,
              acquisition_sources: formData.sources,
              subscription_plan: planId,
              referral_code: formData.referralCode || undefined,
            })
          });
        } else {
          // Existing User: Update Onboarding Profile
          response = await fetch(`${API_BASE_URL}/api/onboarding`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              userId: resolvedUserId,
              trader_type: formData.traderType,
              gender: formData.gender,
              birth_year: formData.birthYear,
              primary_goal: formData.goal,
              acquisition_sources: formData.sources,
              subscription_plan: planId,
              referral_code: formData.referralCode || undefined,
            })
          });
        }

        const data = await response.json();
        if (response.ok) {
          if (data.user) {
            updateFormData({ userId: data.user.id });
          }
          if (data.token) {
            localStorage.setItem('auth_token', data.token);
            updateFormData({ token: data.token });
          }
          // Make sure app_step stays at 4 so back button from pricing returns to Step 4
          localStorage.setItem('app_step', '4');
          window.location.href = '/pricing';
        } else {
          setConnectionError('Error: ' + (data.message || 'Failed to save onboarding data'));
          setIsConnecting(false);
        }
      } catch (e: any) {
        setConnectionError(e.message || 'Network error');
        setIsConnecting(false);
      }
    } else {
      // Free plan: proceed to broker selection (step 5)
      nextStep();
    }
  };

  const handleConnect = async () => {
    let resolvedUserId = formData.userId;
    const token = localStorage.getItem('auth_token');
    
    // Resolve user ID if empty but token is present
    if (!resolvedUserId && token) {
      try {
        const userRes = await fetch(`${API_BASE_URL}/api/user`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        if (userRes.ok) {
          const userData = await userRes.json();
          resolvedUserId = userData.id;
          updateFormData({ userId: userData.id, name: userData.name, email: userData.email });
        }
      } catch (err) {
        console.error('[App] Error resolving user ID in handleConnect:', err);
      }
    }

    if (formData.method === 'auto') {
      if (!formData.apiKey || !formData.apiSecret || (formData.broker === 'bitget' && !formData.apiPassphrase)) {
        setConnectionError(formData.broker === 'bitget' ? 'Please enter API Key, Secret, and Passphrase' : 'Please enter both API Key and Secret');
        return;
      }
      setIsConnecting(true);
      setConnectionError('');

      try {
        // 1. Fetch data from Mock API
        const portfolioData = await apiService.connectBrokerAPI(
          formData.apiKey,
          formData.apiSecret,
          formData.broker,
          formData.apiPassphrase
        );

        // 2. Save data to Global Context
        setPortfolioData(portfolioData);

        // 3. Save onboarding data to backend
        let response;
        if (!resolvedUserId) {
          // New Registration: Create User + Profile
          response = await fetch(`${API_BASE_URL}/api/register-complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              name: formData.name,
              email: formData.email,
              password: formData.password,
              trader_type: formData.traderType,
              gender: formData.gender,
              birth_year: formData.birthYear,
              primary_goal: formData.goal,
              acquisition_sources: formData.sources,
              subscription_plan: formData.plan,
              broker: formData.broker,
              sync_method: formData.method,
              api_key: formData.apiKey,
              api_secret: formData.apiSecret,
              api_passphrase: formData.apiPassphrase,
              referral_code: formData.referralCode,
            })
          });
        } else {
          // Existing User: Update Profile
          response = await fetch(`${API_BASE_URL}/api/onboarding`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              userId: resolvedUserId,
              trader_type: formData.traderType,
              gender: formData.gender,
              birth_year: formData.birthYear,
              primary_goal: formData.goal,
              acquisition_sources: formData.sources,
              subscription_plan: formData.plan,
              broker: formData.broker,
              sync_method: formData.method,
              api_key: formData.apiKey,
              api_secret: formData.apiSecret,
              api_passphrase: formData.apiPassphrase,
              referral_code: formData.referralCode,
            })
          });
        }

        const data = await response.json();
        if (response.ok) {
          if (data.user) {
            updateFormData({ userId: data.user.id });
          }
          if (data.token) {
            localStorage.setItem('auth_token', data.token);
            updateFormData({ token: data.token });
          }
          localStorage.setItem('has_cex_accounts', 'true');
          localStorage.removeItem('cc_preview_api');
          if (formData.plan === 'pro' || formData.plan === 'premium') {
            window.location.href = '/pricing';
          } else {
            window.location.href = '/dashboard';
          }
        } else {
          setConnectionError('Error: ' + (data.message || 'Validation failed'));
          setIsConnecting(false);
        }
      } catch (e: any) {
        setConnectionError(e.message || 'Network error. Is Laravel running?');
        setIsConnecting(false);
      }
    } else {
      // Manual Method - Still needs registration if no userId
      if (!resolvedUserId) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/register-complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              name: formData.name,
              email: formData.email,
              password: formData.password,
              trader_type: formData.traderType,
              gender: formData.gender,
              birth_year: formData.birthYear,
              primary_goal: formData.goal,
              acquisition_sources: formData.sources,
              subscription_plan: formData.plan,
              broker: formData.broker,
              sync_method: formData.method,
              referral_code: formData.referralCode || undefined,
            })
          });
          const data = await response.json();
          if (response.ok) {
            updateFormData({ userId: data.user.id });
            if (data.token) {
              localStorage.setItem('auth_token', data.token);
              updateFormData({ token: data.token });
            }
            localStorage.setItem('has_cex_accounts', 'true');
            localStorage.removeItem('cc_preview_api');
            if (formData.plan === 'pro' || formData.plan === 'premium') {
              window.location.href = '/pricing';
            } else {
              window.location.href = '/dashboard';
            }
          } else {
            setConnectionError(data.message || 'Registration failed');
          }
        } catch (e: any) {
          setConnectionError(e.message || 'Network error');
        }
      } else {
        // Existing User choosing Manual: We still need to call /api/onboarding to save manual CEX account & profile!
        setIsConnecting(true);
        setConnectionError('');
        try {
          const response = await fetch(`${API_BASE_URL}/api/onboarding`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              userId: resolvedUserId,
              trader_type: formData.traderType,
              gender: formData.gender,
              birth_year: formData.birthYear,
              primary_goal: formData.goal,
              acquisition_sources: formData.sources,
              subscription_plan: formData.plan,
              broker: formData.broker,
              sync_method: formData.method,
              referral_code: formData.referralCode || undefined,
            })
          });
          const data = await response.json();
          if (response.ok) {
            localStorage.setItem('has_cex_accounts', 'true');
            localStorage.removeItem('cc_preview_api');
            if (formData.plan === 'pro' || formData.plan === 'premium') {
              window.location.href = '/pricing';
            } else {
              window.location.href = '/dashboard';
            }
          } else {
            setConnectionError(data.message || 'Failed to complete onboarding');
            setIsConnecting(false);
          }
        } catch (e: any) {
          setConnectionError(e.message || 'Network error');
          setIsConnecting(false);
        }
      }
    }
  };

  const handleLogin = async (email: string, pass: string) => {
    try {
      // Clear old session cache first to prevent cross-account leaks
      setPortfolioData(null);
      setLastSyncedAccountId(null);
      localStorage.removeItem('portfolio_data');
      localStorage.removeItem('lastSyncedAccountId');
      localStorage.removeItem('selectedAccountId');
      localStorage.removeItem('subscription_data');
      localStorage.removeItem('has_cex_accounts');

      const data = await apiService.login(email, pass);

      // Save token to localStorage for API calls
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('has_cex_accounts', data.has_cex_accounts ? 'true' : 'false');

      updateFormData({
        userId: data.user.id,
        name: data.user.name,
        email: data.user.email,
        token: data.token
      });
      setUserName(data.user.name);

      if (data.has_cex_accounts) {
        return '/dashboard';
      } else {
        const hasProfile = data.profile && Object.keys(data.profile).length > 0;
        if (hasProfile) {
          updateFormData({
            traderType: data.profile.trader_type,
            gender: data.profile.gender,
            birthYear: data.profile.birth_year,
            goal: data.profile.primary_goal,
            sources: data.profile.acquisition_sources,
            plan: data.profile.subscription_plan,
            broker: data.profile.broker,
            method: data.profile.sync_method,
          });
          setStep(5);
        } else {
          setStep(2);
        }
        return '/onboarding';
      }
    } catch (err: any) {
      throw err;
    }
  };

  const { showManualWizard, setShowManualWizard, showDexWizard, setShowDexWizard } = useCexAccount();

  return (
    <LenisProvider lenisRef={lenisRef}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<LandingPage />} />
        <Route path="/partnership" element={<PartnershipPage />} />
        <Route path="/contact" element={<LandingPage />} />
        <Route path="/privacy" element={<LandingPage />} />
        <Route path="/terms" element={<LandingPage />} />
        <Route
          path="/login"
          element={
            <AuthPage
              formData={formData}
              updateFormData={updateFormData}
              handleRegister={handleRegister}
              handleLogin={handleLogin}
              setStep={setStep}
              initialMode="login"
            />
          }
        />
        <Route
          path="/register"
          element={
            <AuthPage
              formData={formData}
              updateFormData={updateFormData}
              handleRegister={handleRegister}
              handleLogin={handleLogin}
              setStep={setStep}
              initialMode="register"
            />
          }
        />
        <Route
          path="/onboarding"
          element={
            !localStorage.getItem('auth_token') ? (
              <Navigate to="/register" replace />
            ) : (
              <OnboardingPage
                step={step < 2 ? 2 : step}
                subStep={subStep}
                formData={formData}
                updateFormData={updateFormData}
                updateTradeData={updateTradeData}
                nextStep={nextStep}
                prevStep={prevStep}
                handleConnect={handleConnect}
                isConnecting={isConnecting}
                connectionError={connectionError}
                handleSelectPlan={handleSelectPlan}
              />
            )
          }
        />
        <Route
          path="/referral"
          element={
            !localStorage.getItem('auth_token') ? (
              <Navigate to="/register" replace />
            ) : (
              <ReferralPage />
            )
          }
        />
        <Route
          path="/dashboard"
          element={<DashboardPage formData={formData} />}
        />
        <Route
          path="/demo/dashboard"
          element={<DashboardDemo />}
        />
        <Route
          path="/demo/trades"
          element={<TradesDemo />}
        />
        <Route
          path="/preview/dashboard"
          element={<PreviewDashboard />}
        />
        <Route
          path="/reports"
          element={<ReportsPage formData={formData} />}
        />
        <Route
          path="/trades"
          element={
            <TradesPage
              formData={formData}
              pendingTrade={pendingTrade}
              onPendingTradeConsumed={() => setPendingTrade(null)}
            />
          }
        />
        <Route
          path="/notebook"
          element={<NotebookPage formData={formData} />}
        />
        <Route
          path="/pricing"
          element={<PricingPage />}
        />
        <Route
          path="/changelog"
          element={<ChangelogPage />}
        />
        <Route
          path="/ai-assistant"
          element={<AIAssistantPage formData={formData} />}
        />
        <Route
          path="/news"
          element={<NewsPage />}
        />
        <Route
          path="/dev-picks"
          element={<DevPicksPage />}
        />
        <Route
          path="/trending"
          element={<TrendingTokensPage />}
        />
        <Route
          path="/top-traders"
          element={<TopTradersPage />}
        />
        <Route
          path="/faq"
          element={<FAQPage formData={formData} />}
        />
        <Route
          path="/verify-email"
          element={<VerifyEmailPage />}
        />
      </Routes>

      {/* Global Manual Trade Wizard — works from any page */}
      <AnimatePresence>
        {showManualWizard && (
          <AddManualTradeWizard
            onClose={() => setShowManualWizard(false)}
            onSuccess={(newTrade) => {
              setShowManualWizard(false);
              setPendingTrade(newTrade);
              navigate('/trades');
            }}
          />
        )}
      </AnimatePresence>

      {/* Global DEX Trade Wizard */}
      <AnimatePresence>
        {showDexWizard && (
          <AddDexTradeWizard
            onClose={() => setShowDexWizard(false)}
            onSuccess={(newTrade) => {
              setShowDexWizard(false);
              setPendingTrade(newTrade);
              navigate('/trades');
            }}
          />
        )}
      </AnimatePresence>

      {/* Global Neo AI Floating Widget */}
      <NeoAIWidget />

      {/* Update notification — shows once per version after login */}
      <UpdateNotification />
    </LenisProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <SidebarProvider>
      <PortfolioProvider>
        <CexAccountProvider>
          <SubscriptionProvider>
            <Router>
              <AppContent />
            </Router>
          </SubscriptionProvider>
        </CexAccountProvider>
      </PortfolioProvider>
      </SidebarProvider>
    </ThemeProvider>
  );
}
